package fyi.moko.hotpl.ui.friends

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fyi.moko.hotpl.domain.service.AuthService
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * Drives the Friends tab gate: the tab stays visible but locked while the session is
 * anonymous, because friendships are keyed to the uid and an anonymous uid is lost on
 * reinstall. Pull-based like [fyi.moko.hotpl.ui.account.AccountViewModel] —
 * `linkWithGoogle`/`linkWithApple` preserve the uid, so `currentUserId` never emits on
 * link; the Home screen calls [refresh] whenever it (re-)enters composition instead.
 */
class FriendsGateViewModel(
    private val authService: AuthService,
) : ViewModel() {

    // Starts unlocked so linked users never see a lock flash; an anonymous session
    // locks the tab as soon as the first refresh lands.
    private val _isAnonymous = MutableStateFlow(false)
    val isAnonymous: StateFlow<Boolean> = _isAnonymous.asStateFlow()

    init {
        refresh()
        // Also re-check on every auth state change: on the web the persisted user is restored
        // asynchronously after page load, so the init refresh above runs before any user exists
        // and reads as anonymous. Without this the tab stays locked until Home recomposes
        // (visiting Account and coming back) — which reads as "I got logged out" (0-41).
        viewModelScope.launch {
            authService.currentUserId.collect { _isAnonymous.value = authService.isAnonymous() }
        }
    }

    fun refresh() {
        viewModelScope.launch {
            _isAnonymous.value = authService.isAnonymous()
        }
    }
}
